/* Read-only presentation. Redeemly alone schedules and awards the promotion. */
export function parseTimestamp(value) {
  if (typeof value !== 'string') return NaN;
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!parts) return NaN;
  const [, year, month, day, hour, minute, second, zone] = parts;
  const lastDay = new Date(Date.UTC(+year, +month, 0)).getUTCDate();
  if (+year < 2000 || +month < 1 || +month > 12 || +day < 1 || +day > lastDay || +hour > 23 || +minute > 59 || +second > 59) return NaN;
  if (zone !== 'Z' && (+zone.slice(1, 3) > 14 || +zone.slice(4) > 59 || (+zone.slice(1, 3) === 14 && +zone.slice(4) !== 0))) return NaN;
  return Date.parse(value);
}

const zoneFormatters = new Map();

function zoneFormatter(timeZone) {
  if (typeof timeZone !== 'string' || !timeZone || timeZone.length > 100) return null;
  if (zoneFormatters.has(timeZone)) return zoneFormatters.get(timeZone);
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    zoneFormatters.set(timeZone, formatter);
    return formatter;
  } catch {
    return null;
  }
}

function zonedParts(timestamp, timeZone) {
  const formatter = zoneFormatter(timeZone);
  if (!formatter || !Number.isFinite(timestamp)) return null;
  const values = {};
  for (const part of formatter.formatToParts(timestamp)) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  if (![values.year, values.month, values.day, values.hour, values.minute, values.second].every(Number.isFinite)) return null;
  return values;
}

function parseClock(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!match || +match[1] > 23 || +match[2] > 59) return null;
  return { hour: +match[1], minute: +match[2] };
}

function calendarDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

function shiftMonth(year, month, amount) {
  const date = new Date(Date.UTC(year, month - 1 + amount, 1));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
}

function wallTimeToTimestamp(date, clock, timeZone) {
  const target = Date.UTC(date.year, date.month - 1, date.day, clock.hour, clock.minute, 0);
  let timestamp = target;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = zonedParts(timestamp, timeZone);
    if (!parts) return NaN;
    const represented = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const adjusted = target - (represented - timestamp);
    if (adjusted === timestamp) break;
    timestamp = adjusted;
  }
  const result = zonedParts(timestamp, timeZone);
  if (!result || result.year !== date.year || result.month !== date.month || result.day !== date.day || result.hour !== clock.hour || result.minute !== clock.minute || result.second !== 0) return NaN;
  return timestamp;
}

function normalizeRecurrence(config) {
  const recurrence = config?.recurrence;
  const starts = recurrence?.starts;
  const ends = recurrence?.ends;
  const startClock = parseClock(starts?.at);
  const endClock = parseClock(ends?.at);
  const startWeekday = starts?.on?.weekday;
  const endWeekday = ends?.on?.weekday;
  if (recurrence?.every !== 'month' || starts?.on?.kind !== 'nthWeekday' || starts?.on?.n !== 'last' || ends?.on?.kind !== 'weekday') return null;
  if (!startClock || !endClock || !Number.isInteger(startWeekday) || startWeekday < 0 || startWeekday > 6 || !Number.isInteger(endWeekday) || endWeekday < 0 || endWeekday > 6) return null;
  if (!zoneFormatter(config.timeZone)) return null;
  return { startClock, endClock, startWeekday, endWeekday, timeZone: config.timeZone };
}

function recurringWindowForMonth(year, month, recurrence) {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastWeekday = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
  const startDate = calendarDate(year, month, lastDay - (lastWeekday - recurrence.startWeekday + 7) % 7);
  let daysToEnd = (recurrence.endWeekday - recurrence.startWeekday + 7) % 7;
  const startMinutes = recurrence.startClock.hour * 60 + recurrence.startClock.minute;
  const endMinutes = recurrence.endClock.hour * 60 + recurrence.endClock.minute;
  if (daysToEnd === 0 && endMinutes <= startMinutes) daysToEnd = 7;
  const endDate = calendarDate(startDate.year, startDate.month, startDate.day + daysToEnd);
  const start = wallTimeToTimestamp(startDate, recurrence.startClock, recurrence.timeZone);
  const end = wallTimeToTimestamp(endDate, recurrence.endClock, recurrence.timeZone);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return { start, end, timeZone: recurrence.timeZone, source: 'recurrence' };
}

export function campaignWindow(config, now = Date.now()) {
  if (!Number.isFinite(now)) return null;
  const hasStart = config?.start !== null && config?.start !== undefined && config?.start !== '';
  const hasEnd = config?.end !== null && config?.end !== undefined && config?.end !== '';
  if (hasStart || hasEnd) {
    const start = parseTimestamp(config?.start);
    const end = parseTimestamp(config?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    const timeZone = zoneFormatter(config?.timeZone) ? config.timeZone : 'Europe/Lisbon';
    return { start, end, timeZone, source: 'explicit' };
  }
  const recurrence = normalizeRecurrence(config);
  const current = recurrence && zonedParts(now, recurrence.timeZone);
  if (!recurrence || !current) return null;
  const windows = [-1, 0, 1].map(offset => {
    const month = shiftMonth(current.year, current.month, offset);
    return recurringWindowForMonth(month.year, month.month, recurrence);
  }).filter(Boolean);
  const active = windows.find(window => now >= window.start && now < window.end);
  if (active) return active;
  return windows.filter(window => window.start > now).sort((a, b) => a.start - b.start)[0] || null;
}

export function campaignState(config, now = Date.now()) {
  if (config?.source !== 'redeemly' || String(config?.campaignId) !== '175') return 'unavailable';
  if (config.status !== 'enabled') return 'disabled';
  if (config.allowed !== true) return 'ineligible';
  if (config.type !== 'on_order' || config.trigger !== 'storewide' || config.audience !== 'all' || config.rewardType !== 'percentage' || !Number.isFinite(config.percentage) || config.percentage <= 0 || config.percentage > 100) return 'unsupported';
  const window = campaignWindow(config, now);
  if (!window) return 'unavailable';
  if (now >= window.end) return 'ended';
  return now >= window.start ? 'active' : 'scheduled';
}

const defaultCampaignCopy = {
  live_label: 'BOOST PRIME',
  title: 'Acumule __BT_rate__% em saldo Brinca Prime.',
  description_double: 'O dobro dos habituais __BT_base_rate__%, para usar em compras futuras na Brincatoys.',
  description: 'Acumule saldo em todas as compras online para usar em compras futuras na Brincatoys.',
  period: '__BT_starts__ a __BT_ends__ · Hora de Lisboa',
  bar: 'Boost Prime: acumule __BT_rate__% em saldo Brinca Prime em todas as compras online',
  // Live hero keeps exclusions out of the scene; details stay on the conditions page.
  example: '',
  preview_draft_label: 'SIMULAÇÃO DO RASCUNHO',
  preview_editor_label: 'SIMULAÇÃO DO EDITOR',
  preview_label: '__BT_label__ · BOOST PRIME',
  preview_bar: '__BT_label__ · Boost Prime: acumule __BT_rate__% em saldo Brinca Prime. Campanha real não ativada.',
  preview_period: 'Período de referência: __BT_period__',
  preview_example: 'Pré-visualização do aspeto da campanha. Esta simulação não ativa a promoção nem adiciona saldo Brinca Prime.',
};

function campaignText(config, key, values = {}) {
  const translated = config?.copy?.[key];
  const providerNamedExample = key === 'example' && typeof translated === 'string' && /redeemly/i.test(translated);
  // Empty live example is intentional (no fee/tax fine print on the public scene).
  const useTranslated = typeof translated === 'string' && !providerNamedExample && (translated.trim() || key === 'example');
  const template = useTranslated ? translated : defaultCampaignCopy[key];
  return template.replace(/__BT_(rate|base_rate|starts|ends|period|label)__/g, (marker, name) => values[name] ?? marker);
}

function campaignFormatters(config, timeZone) {
  const formats = locale => ({
    date: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone }),
    number: new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }),
  });
  try { return formats(config?.locale || 'pt-PT'); }
  catch { return formats('pt-PT'); }
}

export function campaignCopy(config, now = Date.now()) {
  const window = campaignWindow(config, now);
  if (!window) return null;
  const { date, number } = campaignFormatters(config, window.timeZone);
  const rate = number.format(config.percentage);
  const values = {
    rate,
    base_rate: number.format(config.basePercentage),
    starts: date.format(window.start),
    ends: date.format(window.end - 1),
  };
  const double = Number.isFinite(config.basePercentage) && config.basePercentage > 0 && config.percentage === config.basePercentage * 2;
  return {
    label: campaignText(config, 'live_label'),
    title: campaignText(config, 'title', values),
    description: campaignText(config, double ? 'description_double' : 'description', values),
    period: campaignText(config, 'period', values),
    bar: campaignText(config, 'bar', values),
    percentage: rate + '%',
    example: campaignText(config, 'example'),
  };
}

export function previewCopy(config, scope, now = Date.now()) {
  const copy = campaignCopy(config, now);
  if (!copy) return null;
  const label = campaignText(config, scope === 'draft' ? 'preview_draft_label' : 'preview_editor_label');
  const values = { label, rate: campaignFormatters(config, 'Europe/Lisbon').number.format(config.percentage), period: copy.period };
  return {
    ...copy,
    label: campaignText(config, 'preview_label', values),
    bar: campaignText(config, 'preview_bar', values),
    period: campaignText(config, 'preview_period', values),
    example: campaignText(config, 'preview_example'),
  };
}

const previewSheets = new WeakMap();
const previewFallbackCss = new WeakMap();
const previewFallbackObservers = new WeakMap();
const previewStyleId = 'bt-redeemly-preview-clearance';

export function redeemlyPreviewClearanceCss(previewHeight) {
  const measuredHeight = Number(previewHeight);
  if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return '';
  const barHeight = Math.min(200, Math.ceil(measuredHeight));
  const buttonBottom = barHeight + 16;
  const desktopBottom = barHeight + 76;
  const desktopRoom = desktopBottom + 16;
  return [
    `.launcher__floating-button { bottom: ${buttonBottom}px !important; }`,
    `.launcher__content-container--desktop { bottom: ${desktopBottom}px !important; max-height: min(600px, calc(100vh - ${desktopRoom}px)) !important; max-height: min(600px, calc(100dvh - ${desktopRoom}px)) !important; }`,
    `.launcher__content-container--mobile { bottom: ${barHeight}px !important; --bt-prime-preview-height: ${barHeight}px; max-height: calc(90vh - ${barHeight}px) !important; max-height: calc(90dvh - ${barHeight}px) !important; }`,
    `.launcher__content-container--mobile.translate-y-full { bottom: 0 !important; }`,
  ].join('\n');
}

export function installRedeemlyPreviewClearance(doc = globalThis.document) {
  const previewBar = doc?.getElementById?.('PBarNextFrameWrapper');
  const shadowRoot = doc?.getElementById?.('mcb-launcher-widget-root')?.shadowRoot;
  const css = redeemlyPreviewClearanceCss(previewBar?.getBoundingClientRect?.().height);
  if (!previewBar || !shadowRoot || !css) return false;

  const StyleSheet = doc.defaultView?.CSSStyleSheet || globalThis.CSSStyleSheet;
  if (typeof StyleSheet === 'function' && 'adoptedStyleSheets' in shadowRoot) {
    try {
      let sheet = previewSheets.get(shadowRoot);
      if (!sheet) {
        sheet = new StyleSheet();
        previewSheets.set(shadowRoot, sheet);
      }
      sheet.replaceSync(css);
      if (!shadowRoot.adoptedStyleSheets.includes(sheet)) {
        shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
      }
      return true;
    } catch {
      // Older browsers use the resilient style-element fallback below.
    }
  }

  previewFallbackCss.set(shadowRoot, css);
  const ensureFallbackStyle = () => {
    let style = shadowRoot.getElementById(previewStyleId);
    if (!style) {
      style = doc.createElement('style');
      style.id = previewStyleId;
      shadowRoot.append(style);
    }
    style.textContent = previewFallbackCss.get(shadowRoot);
  };
  ensureFallbackStyle();
  if (!previewFallbackObservers.has(shadowRoot) && typeof globalThis.MutationObserver === 'function') {
    const observer = new MutationObserver(ensureFallbackStyle);
    observer.observe(shadowRoot, { childList: true });
    previewFallbackObservers.set(shadowRoot, observer);
  }
  return true;
}

export function startRedeemlyPreviewClearance(doc = globalThis.document, scope = globalThis) {
  if (!doc?.getElementById) return false;
  const themeRole = scope.Shopify?.theme?.role;
  const isPreview = Boolean(doc.getElementById('PBarNextFrameWrapper')) || Boolean(themeRole && themeRole !== 'main');
  if (!isPreview) return false;
  if (installRedeemlyPreviewClearance(doc)) return true;
  if (typeof scope.setInterval !== 'function') return false;

  let attempts = 0;
  const timer = scope.setInterval(() => {
    attempts += 1;
    if (installRedeemlyPreviewClearance(doc) || attempts >= 200) scope.clearInterval(timer);
  }, 100);
  return true;
}

// Redeemly puts its mobile overlay above its own close toggle. Keep that native
// toggle (and its state/scroll cleanup) reachable in a reserved strip at the top.
export const redeemlyMobileCloseCss = `
:host([data-bt-launcher-suppressed]) .launcher__floating-button:not(.launcher__floating-button--open) {
  visibility: hidden !important;
  pointer-events: none !important;
}
:host([data-bt-launcher-raised]) .launcher__floating-button:not(.launcher__floating-button--open) {
  bottom: var(--bt-prime-launcher-bottom) !important;
}
:host([data-bt-launcher-balance]) .launcher__floating-button:not(.launcher__floating-button--open) .launcher__floating-button-icon {
  display: none !important;
}
@media (max-width: 1099px) {
  :host(:not([data-bt-launcher-balance])) .launcher__floating-button:not(.launcher__floating-button--open) {
    width: 44px !important;
    min-width: 44px !important;
    max-width: 44px !important;
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 !important;
    border-radius: 50% !important;
  }
  :host(:not([data-bt-launcher-balance])) .launcher__floating-button:not(.launcher__floating-button--open) .launcher__floating-button-content {
    gap: 0 !important;
  }
  :host(:not([data-bt-launcher-balance])) .launcher__floating-button:not(.launcher__floating-button--open) .launcher__floating-button-text {
    display: none !important;
  }
  :host([data-bt-launcher-balance]) .launcher__floating-button:not(.launcher__floating-button--open) {
    width: auto !important;
    min-height: 44px !important;
    max-width: calc(100vw - 32px) !important;
    padding: 10px 14px !important;
    border-radius: 999px !important;
  }
  :host([data-bt-launcher-balance]) .launcher__floating-button-text {
    display: block !important;
    min-width: 0;
    font-size: 14px !important;
    overflow-wrap: anywhere;
  }
}
@media (min-width: 1100px) and (max-width: 1499px) {
  .launcher__floating-button:not(.launcher__floating-button--open) {
    padding-inline: 8px !important;
  }
  .launcher__floating-button-text {
    font-size: 12px !important;
  }
  .launcher__floating-button-icon {
    width: 18px !important;
    height: 18px !important;
  }
}
@media (max-width: 767px) {
  .launcher__floating-button.launcher__floating-button--open {
    z-index: 2147483647 !important;
    top: max(12px, env(safe-area-inset-top)) !important;
    right: max(16px, env(safe-area-inset-right)) !important;
    bottom: auto !important;
    min-width: 104px;
    min-height: 44px;
    transform: none !important;
  }
  .launcher__floating-button--open .launcher__floating-button-content { display: none; }
  .launcher__floating-button--open::after {
    content: attr(data-bt-close-label) " ×";
    color: #071d49;
    font: 700 16px/1.5 system-ui, sans-serif;
  }
  .launcher__content-container.launcher__content-container--mobile {
    max-height: calc(100vh - 76px - env(safe-area-inset-top) - var(--bt-prime-preview-height, 0px)) !important;
    max-height: calc(100dvh - 76px - env(safe-area-inset-top) - var(--bt-prime-preview-height, 0px)) !important;
  }
}`;

const mobileCloseRoots = new WeakSet();
const launcherControls = new WeakMap();
const footerControls = new WeakMap();

export function launcherBalanceLabel(button) {
  const text = button?.querySelector?.('.launcher__floating-button-text');
  if (!text || text.classList.contains('launcher__floating-button-text--unauthenticated')) return '';
  const label = text.textContent.trim();
  // Read the app's displayed amount, including zero, without calculating or changing credit.
  return /(?:\d[\d\s.,]*[€$£]|[€$£]\s*\d)/.test(label) ? label : '';
}

const launcherObstructionSelector = 'dialog[open], [role="dialog"], [role="alertdialog"], #shopify-pc__banner, .bt-confidence__benefit, .bt-newsletter__form, .bt-product-bundle [data-essential-upsell-element="add-to-cart-button"], #main-cart #checkout, #main-cart .cart__dynamic-checkout-buttons';

export function launcherOverlapsPriorityContent(doc, button, host) {
  if (typeof doc?.elementsFromPoint !== 'function' || typeof button?.getBoundingClientRect !== 'function') return false;
  const rect = button.getBoundingClientRect();
  if (!rect || rect.width <= 0 || rect.height <= 0) return false;

  // Dialogs, purchase controls, and compact benefit cards take priority over
  // a closed launcher. Walk past the shadow host without briefly
  // disabling the launcher to inspect what is beneath it.
  for (const xRatio of [0.2, 0.5, 0.8]) {
    for (const yRatio of [0.2, 0.5, 0.8]) {
      const stack = doc.elementsFromPoint(rect.left + rect.width * xRatio, rect.top + rect.height * yRatio);
      // Document hit-testing retargets shadow children to their host. Walk past
      // that host and plain wrappers to find the page content actually beneath it.
      for (const target of stack || []) {
        if (!target || target === host || host?.contains?.(target)) continue;
        if (target.closest?.(launcherObstructionSelector)) return true;
      }
    }
  }
  return false;
}

// Redeemly launcher copy sometimes advertises the Boost rate as the base programme.
// Prefer the base 4% string; during a live Boost campaign, swap in a temporary tease.
export function resolveLauncherEarnCopy(base, boost, boostActive) {
  if (boostActive && typeof boost === 'string' && boost.trim()) return boost;
  return typeof base === 'string' ? base : '';
}

export function isUnframedPrimeEarnClaim(text) {
  if (typeof text !== 'string') return false;
  if (/Boost\s*Prime/i.test(text)) return false;
  if (!/\b[48]\s*%/.test(text)) return false;
  if (!/(compras?\s+online|online\s+purchases?)/i.test(text)) return false;
  if (!/(receb[ea]|recibe|earn|acumul[ae]|receive)/i.test(text)) return false;
  return true;
}

export function rewriteLauncherEarnClaim(text, base, boost, boostActive) {
  const target = resolveLauncherEarnCopy(base, boost, boostActive);
  if (typeof text !== 'string' || !target.trim()) return text;
  if (text === target) return text;
  if (text === base || (typeof boost === 'string' && boost && text === boost)) return target;
  if (isUnframedPrimeEarnClaim(text)) return target;
  return text;
}

// Compatibility wrapper: bare 8% → base programme copy; Boost-framed copy stays.
export function rewriteUnframedBoostEarnRate(text, replacement) {
  return rewriteLauncherEarnClaim(text, replacement, '', false);
}

export function sanitizeRedeemlyLauncherEarnCopy(root, base, boost = '', boostActive = false) {
  // Older call sites passed a single replacement string (the base 4% copy).
  if (typeof base === 'string' && arguments.length === 2) {
    boost = '';
    boostActive = false;
  }
  if (!root || typeof base !== 'string' || !base.trim()) return 0;
  let changed = 0;
  const visit = node => {
    if (!node) return;
    if (node.nodeType === 3) {
      const next = rewriteLauncherEarnClaim(node.textContent, base, boost, boostActive);
      if (next !== node.textContent) {
        node.textContent = next;
        changed += 1;
      }
      return;
    }
    // ShadowRoot is a DocumentFragment (11); element nodes are 1.
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    const tag = node.tagName;
    if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'NOSCRIPT') return;
    const children = node.childNodes;
    if (!children?.length) return;
    for (let index = 0; index < children.length; index += 1) visit(children[index]);
  };
  visit(root);
  return changed;
}

export function readBoostCampaignConfig(doc = globalThis.document) {
  if (feed.config) return feed.config;
  const node = doc?.querySelector?.('[data-boost-config]');
  if (!node?.textContent) return null;
  try { return JSON.parse(node.textContent); } catch { return null; }
}

export function isLiveBoostCampaignActive(doc = globalThis.document, now = NaN) {
  for (const el of doc?.querySelectorAll?.('bt-boost-prime') || []) {
    if (el.dataset?.state === 'active' && !el.hidden) return true;
  }
  const config = readBoostCampaignConfig(doc);
  if (!config) return false;
  const stamp = Number.isFinite(now)
    ? now
    : (feed.config ? feed.now() : Date.now());
  return campaignState(config, Number.isFinite(stamp) ? stamp : Date.now()) === 'active';
}

export function installRedeemlyControls(doc = globalThis.document) {
  const host = doc?.getElementById?.('mcb-launcher-widget-root');
  const root = host?.shadowRoot;
  if (!root) return false;
  if (launcherControls.has(root)) return true;
  const primeScript = doc.querySelector('[data-bt-prime-close]');
  const closeLabel = primeScript?.dataset?.btPrimeClose;
  const earnCopy = primeScript?.dataset?.btPrimeEarnCopy
    ?? doc.querySelector('[data-bt-prime-earn-copy]')?.dataset?.btPrimeEarnCopy;
  const earnCopyBoost = primeScript?.dataset?.btPrimeEarnCopyBoost
    ?? doc.querySelector('[data-bt-prime-earn-copy-boost]')?.dataset?.btPrimeEarnCopyBoost
    ?? '';
  const applyEarnCopy = () => {
    sanitizeRedeemlyLauncherEarnCopy(root, earnCopy, earnCopyBoost, isLiveBoostCampaignActive(doc));
  };
  const sync = () => {
    // Keep earn claims aligned with the live Boost window if Redeemly re-renders.
    applyEarnCopy();
    const button = root.querySelector('.launcher__floating-button');
    const open = button?.classList.contains('launcher__floating-button--open') ?? false;
    const balanceLabel = launcherBalanceLabel(button);
    host.toggleAttribute('data-bt-launcher-balance', Boolean(balanceLabel));
    const sticky = doc.querySelector('.section-product-page .sticky-cta');
    const rect = sticky?.getBoundingClientRect();
    const view = doc.defaultView;
    const raised = Boolean(view?.innerWidth < 750 && sticky?.classList.contains('bt-sticky-cta--foreground') && rect?.height > 0 && rect.top >= 0 && rect.top < view.innerHeight);
    host.toggleAttribute('data-bt-launcher-raised', raised);
    if (raised) {
      const bottom = `${Math.ceil(view.innerHeight - rect.top + 12)}px`;
      if (host.style.getPropertyValue('--bt-prime-launcher-bottom') !== bottom) host.style.setProperty('--bt-prime-launcher-bottom', bottom);
    } else {
      host.style.removeProperty('--bt-prime-launcher-bottom');
    }
    const suppressed = !open && Boolean(button) && launcherOverlapsPriorityContent(doc, button, host);
    host.toggleAttribute('data-bt-launcher-suppressed', suppressed);
    if (!button) return;
    host.toggleAttribute('data-bt-prime-open', open);
    const label = open && closeLabel ? `${closeLabel} Brinca Prime` : balanceLabel ? `Brinca Prime: ${balanceLabel}` : 'Brinca Prime';
    if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    button.setAttribute('aria-expanded', String(open));
    if (closeLabel) button.setAttribute('data-bt-close-label', closeLabel);
    root.querySelectorAll('.launcher__overlay').forEach(overlay => { overlay.inert = !open; });
    host.setAttribute('data-bt-prime-ready', '');
  };
  let syncFramePending = false;
  const scheduleSync = () => {
    const requestFrame = doc.defaultView?.requestAnimationFrame;
    if (typeof requestFrame !== 'function') {
      sync();
      return;
    }
    if (syncFramePending) return;
    syncFramePending = true;
    requestFrame.call(doc.defaultView, () => {
      syncFramePending = false;
      sync();
    });
  };
  const onKeyDown = event => {
    if (event.key !== 'Escape' || !host.hasAttribute('data-bt-prime-open')) return;
    event.preventDefault();
    root.querySelector('.launcher__floating-button')?.click();
  };
  const onDialogTransition = event => {
    if (event.target?.closest?.(launcherObstructionSelector)) scheduleSync();
  };
  const Observer = doc.defaultView?.MutationObserver;
  const observer = Observer
    ? new Observer(() => {
      applyEarnCopy();
      scheduleSync();
    })
    : null;
  observer?.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-label'] });
  const sticky = doc.querySelector('.section-product-page .sticky-cta');
  if (sticky) {
    observer?.observe(sticky, { attributes: true, attributeFilter: ['class'] });
    const Resize = doc.defaultView?.ResizeObserver;
    if (Resize) new Resize(scheduleSync).observe(sticky);
  }
  for (const el of doc.querySelectorAll?.('bt-boost-prime') || []) {
    observer?.observe(el, { attributes: true, attributeFilter: ['hidden', 'data-state'] });
  }
  doc.addEventListener('keydown', onKeyDown);
  doc.addEventListener('bt:footer-visibility', scheduleSync);
  // Keyboard dismissal does not produce a click or scroll. Recheck at the
  // theme's dialog lifecycle boundaries and when an overlay finishes moving.
  for (const event of ['cart-drawer-open', 'cart-drawer-closed', 'quick-view-open', 'quick-view-closed']) {
    doc.addEventListener(event, scheduleSync);
  }
  doc.addEventListener('transitionend', onDialogTransition, { capture: true });
  doc.addEventListener('transitioncancel', onDialogTransition, { capture: true });
  doc.addEventListener('scroll', scheduleSync, { capture: true, passive: true });
  doc.addEventListener('click', scheduleSync, { capture: true });
  doc.defaultView?.addEventListener?.('scroll', scheduleSync, { passive: true });
  doc.defaultView?.addEventListener?.('resize', scheduleSync, { passive: true });
  doc.defaultView?.addEventListener?.('pageshow', scheduleSync);
  doc.defaultView?.addEventListener?.('popstate', scheduleSync);
  doc.defaultView?.addEventListener?.('hashchange', scheduleSync);
  doc.defaultView?.visualViewport?.addEventListener?.('scroll', scheduleSync, { passive: true });
  doc.defaultView?.visualViewport?.addEventListener?.('resize', scheduleSync, { passive: true });
  doc.addEventListener('load', scheduleSync, true);
  launcherControls.set(root, { observer, onKeyDown, onDialogTransition, scheduleSync });
  sync();
  return true;
}

export function startFooterVisibility(doc = globalThis.document, scope = globalThis) {
  if (!doc?.documentElement || footerControls.has(doc)) return false;
  const footer = doc.querySelector('.bt-site-footer');
  if (!footer || typeof scope.IntersectionObserver !== 'function') return false;
  const observer = new scope.IntersectionObserver(entries => {
    const visible = entries.some(entry => entry.isIntersecting);
    if (doc.documentElement.hasAttribute('data-bt-footer-visible') === visible) return;
    doc.documentElement.toggleAttribute('data-bt-footer-visible', visible);
    doc.dispatchEvent(new scope.Event('bt:footer-visibility'));
  });
  observer.observe(footer);
  const onClick = event => {
    if (!event.target.closest?.('[data-bt-footer-top]')) return;
    const topButton = doc.querySelector('scroll-to-top-button');
    if (typeof topButton?.scrollToTop !== 'function') return;
    event.preventDefault();
    topButton.scrollToTop();
  };
  doc.addEventListener('click', onClick);
  footerControls.set(doc, observer);
  return true;
}

export function installRedeemlyMobileClose(doc = globalThis.document) {
  const root = doc?.getElementById?.('mcb-launcher-widget-root')?.shadowRoot;
  if (!root) return false;
  if (mobileCloseRoots.has(root)) {
    installRedeemlyControls(doc);
    return true;
  }
  const StyleSheet = doc.defaultView?.CSSStyleSheet;
  if (typeof StyleSheet === 'function' && 'adoptedStyleSheets' in root) {
    try {
      const sheet = new StyleSheet();
      sheet.replaceSync(redeemlyMobileCloseCss);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      mobileCloseRoots.add(root);
      installRedeemlyControls(doc);
      return true;
    } catch { /* Use a style element on browsers without constructable sheets. */ }
  }
  const ensureStyle = () => {
    if (root.getElementById('bt-redeemly-mobile-close')) return;
    const style = doc.createElement('style');
    style.id = 'bt-redeemly-mobile-close';
    style.textContent = redeemlyMobileCloseCss;
    root.append(style);
  };
  ensureStyle();
  const Observer = doc.defaultView?.MutationObserver;
  if (Observer) new Observer(ensureStyle).observe(root, { childList: true });
  mobileCloseRoots.add(root);
  installRedeemlyControls(doc);
  return true;
}

export function startRedeemlyMobileClose(doc = globalThis.document, scope = globalThis) {
  if (!doc?.getElementById) return false;
  if (installRedeemlyMobileClose(doc)) return true;
  if (typeof scope.setInterval !== 'function') return false;
  let attempts = 0;
  const timer = scope.setInterval(() => {
    if (installRedeemlyMobileClose(doc) || ++attempts >= 200) scope.clearInterval(timer);
  }, 100);
  return true;
}

// One shared, read-only request per minute for header and homepage.
// A failed read clears the source instead of advertising stale rewards.
export class CampaignFeed {
  constructor({ read, clock = () => performance.now(), schedule = (callback, delay) => globalThis.setTimeout(callback, delay), cancel = timer => globalThis.clearTimeout(timer) }) {
    Object.assign(this, { read, clock, schedule, cancel });
    this.listeners = new Set();
    this.visible = true;
  }
  set(config) {
    this.config = config;
    this.receivedAt = this.clock();
    this.listeners.forEach(listener => listener());
  }
  now() {
    return Number.isFinite(this.config?.serverNow) ? this.config.serverNow * 1000 + this.clock() - this.receivedAt : NaN;
  }
  subscribe(listener, initial) {
    if (!this.listeners.size) this.set(initial);
    this.listeners.add(listener);
    listener();
    this.queue();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) this.cancel(this.timer);
    };
  }
  queue() {
    this.cancel(this.timer);
    if (this.listeners.size && this.visible) this.timer = this.schedule(() => this.refresh(), 60000);
  }
  refresh() {
    if (this.pending) return this.pending;
    this.cancel(this.timer);
    this.pending = Promise.resolve().then(() => this.read()).then(config => this.set(config), () => this.set(null)).finally(() => {
      this.pending = null;
      this.queue();
    });
    return this.pending;
  }
  setVisible(visible) {
    const changed = this.visible !== visible;
    this.visible = visible;
    if (!visible) this.cancel(this.timer);
    else if (changed) this.refresh();
  }
}

async function readRedeemlySection() {
  const url = new URL(globalThis.Shopify?.routes?.root || '/', location.origin);
  if (url.origin !== location.origin) throw new Error('Invalid storefront origin');
  url.searchParams.set('section_id', 'brincatoys-boost-prime');
  url.searchParams.set('_bt_boost', Date.now().toString());
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    // Redeemly wraps window.fetch and currently expects the request target to be a string.
    const response = await fetch(url.toString(), { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('Campaign source unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return JSON.parse(doc.querySelector('[data-boost-config]').textContent);
  } finally { clearTimeout(timeout); }
}

const feed = new CampaignFeed({ read: readRedeemlySection });

class BoostPrime extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.hidden = true;
    try { this.initial = JSON.parse(this.querySelector('[data-boost-config]').textContent); }
    catch { return; }
    this.onVisibility = () => {
      feed.setVisible(!document.hidden);
      this.render();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onPageShow = event => { if (event.persisted) feed.refresh(); };
    globalThis.addEventListener('pageshow', this.onPageShow);
    this.unsubscribe = feed.subscribe(() => this.render(), this.initial);
    feed.setVisible(!document.hidden);
  }
  render() {
    clearTimeout(this.timer);
    const config = feed.config;
    const now = feed.now();
    // Server-side Liquid gates previews to the editor or a non-published theme.
    // No query parameter or client storage can enable simulation on a live theme.
    const window = campaignWindow(config, now);
    const preview = this.dataset.preview === 'active' && ['editor', 'draft'].includes(this.dataset.previewScope) && window && campaignState(config, window.start) === 'active';
    const state = preview ? 'active' : campaignState(config, now);
    this.dataset.state = state;
    this.dataset.source = 'redeemly';
    this.hidden = state !== 'active';
    if (!this.hidden) {
      const copy = preview ? previewCopy(config, this.dataset.previewScope, now) : campaignCopy(config, now);
      if (!copy) {
        this.hidden = true;
        return;
      }
      for (const [key, value] of Object.entries(copy)) {
        for (const node of this.querySelectorAll('[data-boost-' + key + ']')) node.textContent = value;
      }
    }
    if (preview || !['scheduled', 'active'].includes(state)) return;
    const boundary = state === 'scheduled' ? window.start : window.end;
    this.timer = setTimeout(() => this.render(), Math.max(1, Math.min(30000, boundary - now)));
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    this.unsubscribe?.();
    document.removeEventListener('visibilitychange', this.onVisibility);
    globalThis.removeEventListener('pageshow', this.onPageShow);
  }
}

if (globalThis.customElements && !customElements.get('bt-boost-prime')) customElements.define('bt-boost-prime', BoostPrime);
if (typeof document !== 'undefined') startRedeemlyPreviewClearance(document, globalThis);
if (typeof document !== 'undefined') startRedeemlyMobileClose(document, globalThis);
if (typeof document !== 'undefined') startFooterVisibility(document, globalThis);
